
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, MapPin, Trophy, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { useUserActivity } from '@/hooks/useUserActivity';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLanguage } from '@/contexts/LanguageContext';

interface RecentActivityProps {
  userId?: string;
}

const RecentActivity = ({ userId }: RecentActivityProps) => {
  const { activities, loading } = useUserActivity(userId);
  const isMobile = useIsMobile();
  const { t, language } = useLanguage();
  const [expanded, setExpanded] = useState(false);

  const displayActivities = isMobile && !expanded ? activities.slice(0, 2) : activities;

  const resultMap: Record<string, string> = {
    'Victoria': language === 'es' ? 'Victoria' : 'Victory',
    'Derrota': language === 'es' ? 'Derrota' : 'Defeat',
    'En curso': language === 'es' ? 'En curso' : 'In progress',
    'Registrado': language === 'es' ? 'Registrado' : 'Registered',
  };

  const getResultColor = (result: string) => {
    switch (result) {
      case 'Victoria':
        return 'bg-green-100 text-green-800';
      case 'Derrota':
        return 'bg-red-100 text-red-800';
      case 'En curso':
        return 'bg-primary/10 text-primary';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const dateLocale = language === 'es' ? 'es-ES' : 'en-US';

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Calendar className="h-4 w-4 text-primary" />
            <span>{t('profile.recentActivity')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {[1, 2].map(i => (
            <div key={i} className="animate-pulse p-3 rounded-lg bg-muted/30">
              <div className="h-4 bg-muted rounded w-2/3 mb-2"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Calendar className="h-4 w-4 text-primary" />
          <span>{t('profile.recentActivity')}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {activities.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <Calendar className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">{t('profile.noActivity')}</p>
            </div>
          ) : (
            <>
              {displayActivities.map((activity) => (
                <div key={activity.id} className="p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="font-medium text-sm text-foreground line-clamp-1">{activity.title}</h3>
                    {activity.result && (
                      <Badge className={`text-xs shrink-0 ${getResultColor(activity.result)}`}>
                        {resultMap[activity.result] || activity.result}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3 shrink-0" />
                    <span className="truncate">{activity.location}</span>
                    <span>•</span>
                    <span>{new Date(activity.date).toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' })}</span>
                  </div>
                </div>
              ))}
              
              {isMobile && activities.length > 2 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full mt-2"
                  onClick={() => setExpanded(!expanded)}
                >
                  {expanded ? (
                    <>{t('profile.viewLess')} <ChevronUp className="h-4 w-4 ml-1" /></>
                  ) : (
                    <>{t('common.viewMore')} ({activities.length - 2}) <ChevronDown className="h-4 w-4 ml-1" /></>
                  )}
                </Button>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default RecentActivity;
